import { useState, SyntheticEvent, FormEvent, useCallback } from "react";
import { useRouter } from "next/router";
import Dropzone from "react-dropzone";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { NextSeo } from "next-seo";
import { throttle } from "throttle-debounce";

import { useAuth } from "context/AuthContext";
import { updateUser, checkUsernameExists } from "pages/api/user";
import { toast } from "react-toastify";
import BasicToast from "components/common/Layout/BasicToast";
import { USER_VALIDATIONS } from "constants/validationRules";
import { Avatar } from "components/common";
import { getInitials } from "utils/textUtils";
import { storage } from "utils/firebase";
import { dataURItoBlob } from "utils/imageUtils";

function Details() {
  const { user } = useAuth();

  const [name, setName] = useState<string>(
    user?.name || user?.displayName || ""
  );
  const [username, setUsername] = useState<string>(user?.username || "");
  const [bio, setBio] = useState<string>("");
  const [profileImage, setProfileImage] = useState(null);
  const [usernameError, setUsernameError] = useState<string>("");

  const [isLoading, setIsLoading] = useState(false);

  const router = useRouter();

  const handleSubmitDetails = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isLoading) return;
    setIsLoading(true);

    const validate = (boolResult: boolean, errorMessage: string): boolean => {
      if (!boolResult) {
        setIsLoading(false);
        toast.error(errorMessage);
        return false;
      }

      return true;
    };

    if (
      !validate(
        name.length >= USER_VALIDATIONS.nameMinLength,
        "Please enter a valid name."
      )
    )
      return;
    if (
      !validate(
        name.length <= USER_VALIDATIONS.nameMaxLength,
        `Your username cannot exceed ${USER_VALIDATIONS.nameMaxLength} characters.`
      )
    )
      return;
    if (
      !validate(
        username.length >= USER_VALIDATIONS.usernameMinLength,
        `Your username needs to be at least ${USER_VALIDATIONS.usernameMinLength} characters.`
      )
    )
      return;
    if (!validate(!usernameError, `Username already exists`)) return;
    if (
      !validate(
        username.length <= USER_VALIDATIONS.usernameMaxLength,
        `Your username cannot exceed ${USER_VALIDATIONS.usernameMaxLength} characters.`
      )
    )
      return;

    if (
      !validate(
        bio.length <= USER_VALIDATIONS.bioMaxLength,
        `Your bio cannot exceed ${USER_VALIDATIONS.bioMaxLength} characters.`
      )
    )
      return;

    if (user?.uid) {
      if (profileImage) {
        const profileStorageRef = ref(storage, `users/${user.uid}/profile`);
        const profileUploadImage =
          profileImage === user?.photoURL
            ? profileImage
            : dataURItoBlob(profileImage);

        const profileUploadTask = uploadBytesResumable(
          profileStorageRef,
          profileUploadImage
        );

        await profileUploadTask.on(
          "state_changed",
          (snapshot) => {},
          (error) => {
            toast.error("There was a problem uploading your profile image.");
            setIsLoading(false);
          },
          () => {
            getDownloadURL(profileUploadTask.snapshot.ref).then(
              (downloadURL) => {
                setProfileImage(downloadURL);

                updateUser({
                  docId: user.uid,
                  details: {
                    profileImageUrl: downloadURL,
                  },
                })
                  .then(() => {})
                  .catch((err) => {
                    toast.error("Something went wrong");
                    console.error(err);
                  });
              }
            );
          }
        );
      }

      await updateUser({
        docId: user.uid,
        details: {
          name,
          username,
          bio,
          isOnboarded: true,
        },
      })
        .then(() => {
          toast.success("Saved!");
          router.push(`/${username}`);
        })
        .catch((err) => {
          toast.error("Something went wrong");
          console.error(err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const handleNameChange = (e: FormEvent<HTMLInputElement>) =>
    setName(e.currentTarget.value);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const checkUsername = useCallback(
    throttle(
      700,
      async (newUsername) => {
        const usernameExists = await checkUsernameExists(newUsername);

        if (usernameExists) {
          setUsernameError("Username already exists");
        } else {
          setUsernameError("");
        }
      },
      { noLeading: true }
    ),
    []
  );

  const handleUsernameChange = (e: FormEvent<HTMLInputElement>) => {
    const newUsername = e.currentTarget.value;

    setUsername(
      e.currentTarget.value.substring(0, USER_VALIDATIONS.usernameMaxLength)
    );

    checkUsername(newUsername);
  };

  const handleBioChange = (e: FormEvent<HTMLTextAreaElement>) =>
    setBio(e.currentTarget.value);

  const onProfilePhotoDrop = useCallback((acceptedFiles) => {
    const reader = new FileReader();
    if (!acceptedFiles.length) toast.error("Unsupported image");

    reader.onabort = () => console.log("File reading was aborted");
    reader.onerror = () => console.log("File reading has failed");
    reader.onload = () => {
      setProfileImage(reader.result);
    };
    reader.readAsDataURL(acceptedFiles[0]);
  }, []);

  const handleRemoveProfileImage = () => setProfileImage(null);

  const isProfileFilled = () => name && username && bio;

  return (
    <>
      <NextSeo title="Onboarding" description="Complete your profile" />
      <main className="h-full">
        <div className="flex min-h-full flex-col items-center justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <h1 className="text-m px-4 py-2 text-center font-normal tracking-tight text-black ">
              Complete your profile
            </h1>
          </div>

          <div className="mt-5 sm:mx-auto sm:w-full sm:max-w-3xl">
            <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
              <div>
                <label
                  htmlFor="name"
                  className="block text-lg font-medium text-black"
                >
                  Profile
                </label>
                <div className="mt-1 text-sm font-medium text-gray-400">
                  This information will be displayed publicly so be careful what
                  you share.
                </div>
              </div>
              <form
                className="mt-6 space-y-6"
                action="#"
                method="POST"
                onSubmit={handleSubmitDetails}
              >
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Name
                  </label>
                  <div className="mt-2">
                    <input
                      id="name"
                      name="name"
                      type="name"
                      maxLength={USER_VALIDATIONS.nameMaxLength}
                      value={name.substring(0, USER_VALIDATIONS.nameMaxLength)}
                      autoComplete="name"
                      required
                      className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-emerald-500  focus:ring-emerald-500 sm:text-sm"
                      onChange={handleNameChange}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="username"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Username
                  </label>
                  <div className="mt-1">
                    <div className="mt-1 flex rounded-md shadow-sm">
                      <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500 sm:text-sm">
                        @
                      </span>
                      <input
                        type="text"
                        name="username"
                        id="username"
                        maxLength={USER_VALIDATIONS.usernameMaxLength}
                        value={username.substring(
                          0,
                          USER_VALIDATIONS.usernameMaxLength
                        )}
                        autoComplete="current-username"
                        required
                        onChange={handleUsernameChange}
                        className="block w-full min-w-0 flex-1 rounded-none rounded-r-md border-gray-300 px-3 py-2 focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm"
                      />
                    </div>
                  </div>
                  <div className="mt-1 text-sm text-red-400">
                    {usernameError}
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="bio"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Bio
                  </label>
                  <div className="mt-1">
                    <textarea
                      rows={4}
                      name="bio"
                      id="bio"
                      required
                      maxLength={USER_VALIDATIONS.bioMaxLength}
                      value={bio.substring(0, USER_VALIDATIONS.bioMaxLength)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm"
                      onChange={handleBioChange}
                    />
                  </div>
                </div>

                <div>
                  <div className="block text-sm font-medium text-gray-700">
                    Photo
                  </div>
                  <div className="mt-2 flex flex-row content-center">
                    <Avatar
                      initials={getInitials(name)}
                      imageUrl={profileImage}
                      size="l"
                    />
                    {profileImage ? (
                      <div className="flex flex-wrap content-center">
                        <button
                          onClick={handleRemoveProfileImage}
                          className="mx-4 cursor-pointer rounded-md border border-gray-300 px-3 py-1 text-sm font-medium hover:bg-gray-100 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <Dropzone
                        onDrop={onProfilePhotoDrop}
                        accept={{
                          "image/*": [".png", ".gif", ".jpeg", ".jpg"],
                        }}
                        maxFiles={1}
                        maxSize={5000000}
                        noClick
                      >
                        {({ getRootProps, getInputProps, open }) => (
                          <div
                            className="mx-4 flex w-full flex-wrap content-center"
                            {...getRootProps()}
                          >
                            <label
                              className="flex h-full w-full flex-wrap content-center text-sm font-medium text-gray-700"
                              htmlFor="profilePhoto"
                            >
                              <input
                                {...getInputProps()}
                                onClick={open}
                                accept="image/*"
                                type="file"
                                id="profilePhoto"
                                className="flex w-full cursor-pointer flex-wrap content-center justify-center text-sm text-gray-900 file:mr-4 file:cursor-pointer file:rounded-md file:border file:border-solid file:border-gray-300 file:bg-white file:px-3 file:py-1 focus:outline-none  file:focus:border-emerald-500 file:focus:outline-none  file:focus:ring-emerald-500 dark:text-gray-400 dark:placeholder-gray-400 "
                              />
                              <span className="cursor-pointer rounded-md border border-solid border-gray-300 bg-white px-3 py-1 text-gray-700 hover:bg-gray-100 focus:border-emerald-500 focus:outline-none focus:ring-emerald-500">
                                Upload
                              </span>
                            </label>
                          </div>
                        )}
                      </Dropzone>
                    )}
                  </div>
                </div>

                <div className="flex w-full justify-end">
                  <button
                    type="submit"
                    disabled={!isProfileFilled || isLoading}
                    className="flex  justify-center rounded-md border border-transparent bg-emerald-500 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-emerald-600 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-emerald-500 disabled:opacity-70"
                  >
                    Create account
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
      <BasicToast />
    </>
  );
}

export default Details;
